/**
 * Funções utilitárias para formatação de documentos (CPF, CNPJ)
 */

/**
 * Formata um valor como CPF: 000.000.000-00
 */
export const formatCpf = (value: string): string => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')
}

/**
 * Formata um valor como CNPJ: 00.000.000/0000-00
 */
export const formatCnpj = (value: string): string => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')
}

/**
 * Detecta se um valor parece ser um email (contém @ ou começa com caracteres que indicam email)
 */
const isEmail = (value: string): boolean => {
  const trimmed = value.trim()
  
  // Se contém @, é email
  if (/@/.test(trimmed)) {
    return true
  }
  
  const digits = trimmed.replace(/\D/g, '')
  
  // Se começa com número, definitivamente não é email
  if (/^\d/.test(trimmed)) {
    return false
  }
  
  // Se tem 11+ dígitos, não é email (é CPF ou CNPJ)
  if (digits.length >= 11) {
    return false
  }
  
  // Se começa com letras e tem caracteres de email, pode ser início de email
  // Mas só se não tiver muitos dígitos
  const hasEmailChars = /[a-zA-Z._-]/.test(trimmed)
  const startsWithLetter = /^[a-zA-Z]/.test(trimmed)
  
  return hasEmailChars && startsWithLetter && digits.length < 11
}

/**
 * Formata um valor automaticamente como CPF ou CNPJ, ou mantém como está se for email
 */
export const formatDocumentOrEmail = (value: string): string => {
  // Se parece ser email, não formata
  if (isEmail(value)) {
    return value
  }

  const digits = value.replace(/\D/g, '')
  
  // Se não tem dígitos, retorna sem formatação
  if (digits.length === 0) {
    return value
  }

  // Prioridade: quantidade de dígitos define o tipo
  // Se tem 12 ou mais dígitos, é CNPJ (mesmo que tenha sido apagado e ainda tenha barra)
  if (digits.length >= 12) {
    return formatCnpj(value)
  }

  // Se tem 11 ou menos dígitos, é CPF (mesmo que ainda tenha caracteres de CNPJ)
  if (digits.length <= 11) {
    // Se tem 5+ dígitos e já tem o padrão inicial de CNPJ formatado (XX.XXX)
    // mas ainda não tem 12 dígitos, verifica se está no meio da digitação
    if (digits.length >= 5 && digits.length < 11 && value.match(/^\d{2}\.\d{3}/) && !value.includes('-') && !value.includes('/')) {
      // Se está no padrão inicial de CNPJ (XX.XXX) sem barra nem traço, pode ser início de CNPJ
      // Mas só se não tiver chegado perto do tamanho de CPF ainda
      return formatCnpj(value)
    }
    
    // Caso contrário, formata como CPF
    return formatCpf(value)
  }

  // Fallback: não deveria chegar aqui, mas se chegar, formata como CPF
  return formatCpf(value)
}

